import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { coachApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MessageCircle, Send, Sparkles, Target, Calendar, Loader2, Lightbulb, TrendingUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AICoachPage() {
  const { isPremium } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionPlan, setActionPlan] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const messagesEndRef = useRef(null);

  const suggestedQuestions = [
    "How can I improve my credit score?",
    "What items should I dispute first?",
    "Explain the 30-day dispute timeline",
    "What's hurting my credit the most?"
  ];

  useEffect(function loadInitialData() {
    async function fetchData() {
      try {
        const insightsData = await coachApi.getInsights();
        setInsights(insightsData);
      } catch (err) {
        console.error('Failed to fetch insights:', err);
      }
      try {
        const historyData = await coachApi.getHistory();
        if (historyData.history && historyData.history.length > 0) {
          const historyMessages = [];
          const reversed = historyData.history.slice().reverse();
          for (let i = 0; i < Math.min(reversed.length, 10); i++) {
            const h = reversed[i];
            historyMessages.push({ role: 'user', content: h.user_message });
            historyMessages.push({ role: 'assistant', content: h.ai_response });
          }
          setMessages(historyMessages);
        }
      } catch (err) {
        console.error('Failed to fetch history:', err);
      }
    }
    fetchData();
  }, []);

  useEffect(function scrollToBottom() {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = useCallback(async function(e) {
    e.preventDefault();
    const trimmedInput = input.trim();
    if (!trimmedInput || loading) return;

    setInput('');
    setMessages(function(prev) { return [...prev, { role: 'user', content: trimmedInput }]; });
    setLoading(true);

    try {
      const response = await coachApi.chat(trimmedInput);
      setMessages(function(prev) { return [...prev, { role: 'assistant', content: response.response }]; });
    } catch (err) {
      toast.error('Failed to get response');
      setMessages(function(prev) { return [...prev, { role: 'assistant', content: "I'm having trouble connecting right now. Please try again in a moment." }]; });
    } finally {
      setLoading(false);
    }
  }, [input, loading]);

  const handleGetActionPlan = useCallback(async function() {
    setLoadingPlan(true);
    try {
      const data = await coachApi.getActionPlan();
      setActionPlan(data.action_plan);
    } catch (err) {
      toast.error('Failed to generate action plan');
    } finally {
      setLoadingPlan(false);
    }
  }, []);

  function handleSuggestedClick(q) {
    setInput(q);
  }

  function renderMessage(msg, index) {
    const isUser = msg.role === 'user';
    const className = isUser 
      ? 'flex justify-end' 
      : 'flex justify-start';
    const bubbleClass = isUser
      ? 'max-w-[80%] rounded-2xl px-4 py-3 bg-slate-900 text-white'
      : 'max-w-[80%] rounded-2xl px-4 py-3 bg-slate-100 text-slate-900';
    
    return (
      <div key={index} className={className}>
        <div className={bubbleClass} data-testid={'message-' + index}>
          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
        </div>
      </div>
    );
  }

  function renderInsightStat(value, label, bgColor, textColor) {
    const classes = bgColor + ' rounded-xl p-4 text-center';
    return (
      <div className={classes}>
        <p className={'text-2xl font-bold ' + (textColor || 'text-slate-900')}>{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    );
  }

  function renderRecommendation(rec, index) {
    const isWarning = rec.type === 'warning';
    const wrapperClass = isWarning 
      ? 'p-4 rounded-xl border bg-amber-50 border-amber-200'
      : 'p-4 rounded-xl border bg-teal-50 border-teal-200';
    const IconComp = isWarning ? AlertCircle : TrendingUp;
    const iconClass = isWarning ? 'h-5 w-5 text-amber-600 mt-0.5' : 'h-5 w-5 text-teal-600 mt-0.5';
    const titleClass = isWarning ? 'font-medium text-amber-800' : 'font-medium text-teal-800';
    const descClass = isWarning ? 'text-sm mt-1 text-amber-700' : 'text-sm mt-1 text-teal-700';

    return (
      <div key={index} className={wrapperClass}>
        <div className="flex items-start gap-3">
          <IconComp className={iconClass} />
          <div>
            <p className={titleClass}>{rec.title}</p>
            <p className={descClass}>{rec.description}</p>
          </div>
        </div>
      </div>
    );
  }

  const hasMessages = messages.length > 0;
  const hasInsights = insights !== null;
  const hasRecommendations = hasInsights && insights.recommendations && insights.recommendations.length > 0;

  return (
    <div className="space-y-6" data-testid="ai-coach-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          AI Credit Coach
        </h1>
        <p className="text-slate-600 mt-1">Get personalized credit advice and guidance.</p>
      </div>

      {!isPremium && (
        <Card className="border-amber-200 bg-amber-50" data-testid="premium-notice">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <span className="text-amber-800">
              Upgrade to Premium for unlimited AI Coach conversations and personalized action plans.
            </span>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="chat" className="space-y-4">
        <TabsList data-testid="coach-tabs">
          <TabsTrigger value="chat" data-testid="chat-tab">
            <MessageCircle className="h-4 w-4 mr-2" />
            Chat
          </TabsTrigger>
          <TabsTrigger value="plan" data-testid="plan-tab">
            <Target className="h-4 w-4 mr-2" />
            Action Plan
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="insights-tab">
            <Lightbulb className="h-4 w-4 mr-2" />
            Insights
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat">
          <Card className="border-slate-100 shadow-sm" data-testid="chat-card">
            <CardContent className="p-0">
              <ScrollArea className="h-[400px] p-4">
                {!hasMessages ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mb-4">
                      <Sparkles className="h-8 w-8 text-teal-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                      Hi, I'm your AI Credit Coach!
                    </h3>
                    <p className="text-slate-500 mb-6 max-w-sm">
                      Ask me anything about credit repair, dispute strategies, or how to improve your score.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {suggestedQuestions.map(function(q, i) {
                        return (
                          <Button
                            key={i}
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={function() { handleSuggestedClick(q); }}
                            data-testid={'suggested-q-' + i}
                          >
                            {q}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map(renderMessage)}
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-slate-100 rounded-2xl px-4 py-3">
                          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              <div className="border-t border-slate-100 p-4">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={input}
                    onChange={function(e) { setInput(e.target.value); }}
                    placeholder="Ask me anything about credit..."
                    className="flex-1 h-11"
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button 
                    type="submit" 
                    className="btn-teal"
                    disabled={loading || !input.trim()}
                    data-testid="send-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plan">
          <Card className="border-slate-100 shadow-sm" data-testid="action-plan-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Target className="h-5 w-5 text-teal-600" />
                Personalized Action Plan
              </CardTitle>
              <CardDescription>
                Get a customized 30/90 day credit improvement plan
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!actionPlan ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-600 mb-4">
                    Generate a personalized action plan based on your credit profile.
                  </p>
                  <Button 
                    className="btn-teal"
                    onClick={handleGetActionPlan}
                    disabled={loadingPlan}
                    data-testid="generate-plan-btn"
                  >
                    {loadingPlan ? 'Generating...' : 'Generate My Plan'}
                  </Button>
                </div>
              ) : (
                <div className="prose prose-slate max-w-none">
                  <div className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
                    {actionPlan}
                  </div>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={handleGetActionPlan}
                    disabled={loadingPlan}
                    data-testid="regenerate-plan-btn"
                  >
                    {loadingPlan ? 'Regenerating...' : 'Regenerate Plan'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights">
          <Card className="border-slate-100 shadow-sm" data-testid="insights-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Lightbulb className="h-5 w-5 text-teal-600" />
                Credit Insights
              </CardTitle>
              <CardDescription>
                Overview of your credit profile and recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hasInsights ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {renderInsightStat(insights.total_items || 0, 'Total Items', 'bg-slate-50')}
                    {renderInsightStat(insights.negative_items || 0, 'Negative Items', 'bg-red-50', 'text-red-600')}
                    {renderInsightStat(insights.disputes_sent || 0, 'Disputes Sent', 'bg-blue-50', 'text-blue-600')}
                    {renderInsightStat((insights.success_rate || 0).toFixed(0) + '%', 'Success Rate', 'bg-green-50', 'text-green-600')}
                  </div>

                  {hasRecommendations && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                        Recommendations
                      </h3>
                      <div className="space-y-3">
                        {insights.recommendations.map(renderRecommendation)}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-slate-300" />
                  <p>Loading insights...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
